import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth, api } from '../contexts/AuthContext';
import { 
  Users,
  Download,
  Loader2,
  TrendingUp,
  Mail,
  Phone,
  Calendar,
  Scissors,
  Search,
  Activity,
  ArrowLeft,
  RefreshCw,
  BarChart3,
  Clock,
  User,
  Stethoscope,
  ChevronLeft,
  ChevronRight,
  Send,
  CheckCircle,
  XCircle,
  Filter,
  UserCheck,
  DollarSign,
  Percent
} from 'lucide-react';

// Tool icons and colors mapping
const TOOL_CONFIG = {
  'csv-doctor': { icon: Stethoscope, color: 'text-red-600', bg: 'bg-red-100', label: 'CSV Doctor' },
  'phone-formatter': { icon: Phone, color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Phone Formatter' },
  'email-formatter': { icon: Mail, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Email Fixer' },
  'name-splitter': { icon: User, color: 'text-purple-600', bg: 'bg-purple-100', label: 'Name Splitter' },
  'file-splitter': { icon: Scissors, color: 'text-indigo-600', bg: 'bg-indigo-100', label: 'File Splitter' }
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subvalue, color, bgColor }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <div className="flex items-start justify-between">
      <div className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center`}>
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
    </div>
    <div className="mt-4">
      <p className="text-3xl font-bold text-slate-900">{value?.toLocaleString() || 0}</p>
      <p className="text-sm text-slate-500 mt-1">{label}</p>
      {subvalue && <p className="text-xs text-emerald-600 mt-1">{subvalue}</p>}
    </div>
  </div>
);

// Conversion Rate Card
const ConversionCard = ({ label, rate, count, total, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-medium text-slate-600">{label}</span>
      <span className={`text-lg font-bold ${color}`}>{rate}%</span>
    </div>
    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
      <div 
        className={`h-full ${color.replace('text-', 'bg-')} transition-all duration-500`}
        style={{ width: `${Math.min(rate, 100)}%` }}
      />
    </div>
    <p className="text-xs text-slate-500 mt-2">{count} of {total} leads</p>
  </div>
);

// Tool Usage Bar with conversion
const ToolUsageBar = ({ tool, data, total }) => {
  const config = TOOL_CONFIG[tool] || { icon: Activity, color: 'text-slate-600', bg: 'bg-slate-100', label: tool };
  const Icon = config.icon;
  const percentage = total > 0 ? Math.round((data.leads / total) * 100) : 0;
  
  return (
    <div className="p-3 bg-slate-50 rounded-lg">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-8 h-8 ${config.bg} rounded-lg flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-slate-700">{config.label}</span>
        </div>
        <span className="text-sm font-bold text-slate-900">{data.leads}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="text-center">
          <p className="text-slate-500">Signups</p>
          <p className="font-semibold text-emerald-600">{data.signups} ({data.signup_rate}%)</p>
        </div>
        <div className="text-center">
          <p className="text-slate-500">Paid</p>
          <p className="font-semibold text-indigo-600">{data.paid_conversions} ({data.paid_rate}%)</p>
        </div>
        <div className="text-center">
          <p className="text-slate-500">Share</p>
          <p className="font-semibold text-slate-600">{percentage}%</p>
        </div>
      </div>
    </div>
  );
};

// Lead Row with checkbox
const LeadRow = ({ lead, selected, onToggle }) => {
  const config = TOOL_CONFIG[lead.tool] || { icon: Activity, color: 'text-slate-600', bg: 'bg-slate-100', label: lead.tool };
  const Icon = config.icon;
  const createdAt = new Date(lead.created_at);
  
  return (
    <tr className="hover:bg-slate-50 transition-colors">
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggle(lead.lead_id)}
          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 ${config.bg} rounded-lg flex items-center justify-center`}>
            <Icon className={`w-4 h-4 ${config.color}`} />
          </div>
          <span className="font-medium text-slate-900">{lead.email}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
          {config.label}
        </span>
      </td>
      <td className="px-4 py-3 text-slate-500 text-sm">
        {createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </td>
    </tr>
  );
};

// Email Composer Modal
const EmailComposerModal = ({ isOpen, onClose, selectedLeads, onSend, sending }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  
  if (!isOpen) return null;
  
  const handleSend = () => {
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!body.trim()) {
      toast.error('Please enter email body');
      return;
    }
    onSend(subject, body);
  };
  
  // Email templates
  const templates = [
    {
      name: 'Free Tool Follow-up',
      subject: 'Did your data cleaning help?',
      body: `<p>Hi there,</p>
<p>I noticed you used our free data cleaning tool recently. I hope it helped improve your CRM data quality!</p>
<p>Did you know you can clean <strong>unlimited rows</strong> with our paid plans? Plus, you get access to:</p>
<ul>
<li>Advanced email validation</li>
<li>Phone number formatting for 200+ countries</li>
<li>Duplicate detection and merging</li>
<li>Name standardization</li>
</ul>
<p><a href="https://aidatacleaner.xyz/pricing" style="color: #4F46E5; font-weight: bold;">See our pricing →</a></p>
<p>Questions? Just reply to this email!</p>
<p>Best,<br>The CRM Data Cleaner Team</p>`
    },
    {
      name: 'Special Offer',
      subject: '🎁 Special offer: 50% off your first month',
      body: `<p>Hi there,</p>
<p>Thanks for trying our free data cleaning tools! As a thank you, here's an exclusive offer:</p>
<p style="background: #EEF2FF; padding: 16px; border-radius: 8px; text-align: center;">
<strong style="font-size: 24px; color: #4F46E5;">50% OFF</strong><br>
<span>Your first month of any plan</span>
</p>
<p>Use code <strong>CLEANDATA50</strong> at checkout.</p>
<p><a href="https://aidatacleaner.xyz/pricing" style="color: #4F46E5; font-weight: bold;">Claim your discount →</a></p>
<p>This offer expires in 7 days.</p>
<p>Best,<br>The CRM Data Cleaner Team</p>`
    }
  ];
  
  const applyTemplate = (template) => {
    setSubject(template.subject);
    setBody(template.body);
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-slate-100">
          <h2 className="text-xl font-bold text-slate-900">Email {selectedLeads.length} Leads</h2>
          <p className="text-sm text-slate-500 mt-1">Send a marketing email to selected leads</p>
        </div>
        
        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          {/* Templates */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Quick Templates</label>
            <div className="flex gap-2 flex-wrap">
              {templates.map((t, i) => (
                <Button
                  key={i}
                  variant="outline"
                  size="sm"
                  onClick={() => applyTemplate(t)}
                >
                  {t.name}
                </Button>
              ))}
            </div>
          </div>
          
          {/* Subject */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Subject Line</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject..."
              className="w-full"
            />
          </div>
          
          {/* Body */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Email Body (HTML)</label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="<p>Your email content here...</p>"
              className="w-full h-64 font-mono text-sm"
            />
          </div>
          
          {/* Preview */}
          {body && (
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Preview</label>
              <div 
                className="border border-slate-200 rounded-lg p-4 bg-white prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: body }}
              />
            </div>
          )}
        </div>
        
        <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button 
            onClick={handleSend} 
            disabled={sending}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send to {selectedLeads.length} Leads
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

const LeadGenDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [conversionStats, setConversionStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTool, setFilterTool] = useState('all');
  const [showNonConverted, setShowNonConverted] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const pageSize = 20;

  // Check admin access
  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Determine which endpoint to use
      const leadsEndpoint = showNonConverted 
        ? '/api/admin/leads/non-converted'
        : '/api/admin/leads';
      
      // Fetch leads, stats, and conversion stats in parallel
      const [leadsRes, statsRes, conversionRes] = await Promise.all([
        api.get(leadsEndpoint, {
          params: { 
            page, 
            page_size: pageSize,
            tool: filterTool !== 'all' ? filterTool : undefined
          }
        }),
        api.get('/api/admin/leads/stats'),
        api.get('/api/admin/leads/conversion-stats')
      ]);
      
      setLeads(leadsRes.data.leads || []);
      setTotalPages(Math.ceil((leadsRes.data.total || 0) / pageSize));
      setStats(statsRes.data);
      setConversionStats(conversionRes.data);
      
    } catch (error) {
      console.error('Failed to fetch lead data:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to load lead generation data');
      }
    } finally {
      setLoading(false);
    }
  }, [page, filterTool, pageSize, showNonConverted]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user, fetchData]);

  // Toggle lead selection
  const toggleLeadSelection = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  // Select all visible leads
  const toggleSelectAll = () => {
    const visibleLeadIds = filteredLeads.map(l => l.lead_id);
    if (selectedLeads.length === visibleLeadIds.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(visibleLeadIds);
    }
  };

  // Send email to selected leads
  const handleSendEmail = async (subject, body) => {
    setSending(true);
    try {
      const response = await api.post('/api/admin/leads/send-email', {
        lead_ids: selectedLeads,
        subject,
        body,
        from_name: 'CRM Data Cleaner'
      });
      
      if (response.data.success) {
        toast.success(`Sent to ${response.data.sent_count} leads!`);
        if (response.data.failed_count > 0) {
          toast.warning(`${response.data.failed_count} emails failed`);
        }
        setShowEmailModal(false);
        setSelectedLeads([]);
      }
    } catch (error) {
      console.error('Email send error:', error);
      toast.error('Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  // Export leads to CSV
  const exportToCSV = () => {
    if (leads.length === 0) {
      toast.error('No leads to export');
      return;
    }
    
    const headers = ['Email', 'Tool', 'Date'];
    const csvContent = [
      headers.join(','),
      ...leads.map(lead => {
        const date = new Date(lead.created_at);
        return [
          lead.email,
          TOOL_CONFIG[lead.tool]?.label || lead.tool,
          date.toLocaleDateString()
        ].join(',');
      })
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leads_${showNonConverted ? 'non_converted_' : ''}${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('Leads exported successfully');
  };

  // Filter leads by search term
  const filteredLeads = leads.filter(lead => 
    lead.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard" className="text-slate-400 hover:text-slate-600">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Lead Generation Dashboard</h1>
                <p className="text-sm text-slate-500">Track, convert, and email leads from free tools</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="outline"
                onClick={exportToCSV}
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Conversion Stats */}
        {conversionStats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              icon={Users}
              label="Total Leads"
              value={conversionStats.total_leads}
              color="text-indigo-600"
              bgColor="bg-indigo-100"
            />
            <StatCard
              icon={UserCheck}
              label="Converted to Signup"
              value={conversionStats.total_signups}
              subvalue={`${conversionStats.signup_rate}% conversion`}
              color="text-emerald-600"
              bgColor="bg-emerald-100"
            />
            <StatCard
              icon={DollarSign}
              label="Paid Customers"
              value={conversionStats.total_paid_conversions}
              subvalue={`${conversionStats.paid_rate}% of leads`}
              color="text-amber-600"
              bgColor="bg-amber-100"
            />
            <StatCard
              icon={Calendar}
              label="Leads Today"
              value={stats?.leads_today || 0}
              color="text-blue-600"
              bgColor="bg-blue-100"
            />
          </div>
        )}

        {/* Conversion Rates */}
        {conversionStats && (
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <ConversionCard
              label="Lead → Signup Rate"
              rate={conversionStats.signup_rate}
              count={conversionStats.total_signups}
              total={conversionStats.total_leads}
              color="text-emerald-600"
            />
            <ConversionCard
              label="Lead → Paid Rate"
              rate={conversionStats.paid_rate}
              count={conversionStats.total_paid_conversions}
              total={conversionStats.total_leads}
              color="text-indigo-600"
            />
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Tool Performance with Conversion */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Tool Performance</h2>
            <div className="space-y-3">
              {conversionStats?.by_tool && Object.entries(conversionStats.by_tool).length > 0 ? (
                Object.entries(conversionStats.by_tool)
                  .sort(([,a], [,b]) => b.leads - a.leads)
                  .map(([tool, data]) => (
                    <ToolUsageBar 
                      key={tool} 
                      tool={tool} 
                      data={data} 
                      total={conversionStats.total_leads} 
                    />
                  ))
              ) : (
                <p className="text-slate-500 text-sm">No tool usage data yet</p>
              )}
            </div>
          </div>

          {/* Leads Table with Selection */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200">
            <div className="p-4 border-b border-slate-100">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold text-slate-900">
                    {showNonConverted ? 'Non-Converted Leads' : 'All Leads'}
                  </h2>
                  <Button
                    variant={showNonConverted ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setShowNonConverted(!showNonConverted);
                      setPage(1);
                      setSelectedLeads([]);
                    }}
                    className={showNonConverted ? "bg-indigo-600 text-white" : ""}
                  >
                    <Filter className="w-3 h-3 mr-1" />
                    {showNonConverted ? 'Non-Converted Only' : 'Show All'}
                  </Button>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Search by email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-full sm:w-48"
                    />
                  </div>
                  
                  <select
                    value={filterTool}
                    onChange={(e) => {
                      setFilterTool(e.target.value);
                      setPage(1);
                      setSelectedLeads([]);
                    }}
                    className="h-10 px-3 border border-slate-200 rounded-lg text-sm text-slate-600 bg-white"
                  >
                    <option value="all">All Tools</option>
                    {Object.entries(TOOL_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Action Bar */}
              {selectedLeads.length > 0 && (
                <div className="mt-4 p-3 bg-indigo-50 rounded-lg flex items-center justify-between">
                  <span className="text-sm text-indigo-700">
                    <strong>{selectedLeads.length}</strong> leads selected
                  </span>
                  <Button
                    onClick={() => setShowEmailModal(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    size="sm"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email Selected
                  </Button>
                </div>
              )}
            </div>
            
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedLeads.length === filteredLeads.length && filteredLeads.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-slate-300 text-indigo-600"
                      />
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Email</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Tool</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredLeads.length > 0 ? (
                    filteredLeads.map((lead) => (
                      <LeadRow 
                        key={lead.lead_id} 
                        lead={lead} 
                        selected={selectedLeads.includes(lead.lead_id)}
                        onToggle={toggleLeadSelection}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-4 py-12 text-center text-slate-500">
                        {searchTerm ? 'No leads match your search' : 'No leads found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-4 border-t border-slate-100 flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Email Composer Modal */}
      <EmailComposerModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        selectedLeads={selectedLeads}
        onSend={handleSendEmail}
        sending={sending}
      />
    </div>
  );
};

export default LeadGenDashboardPage;
